import { useState } from "react";
import { motion } from "framer-motion";
import { Swords, Shield, Zap, Star, Gem, TrendingUp, TrendingDown, Sparkles, Wrench, HeartPulse, Hourglass, ShieldPlus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { ABILITIES, ELEMENT_META, MOVE_ICONS } from "@/components/ArenaBits";

// ── source colour coding for the "where it comes from" tags ──
const SRC_TONE = {
  NATURAL: "text-cyan-300 border-cyan-400/40 bg-cyan-400/10",
  LVL: "text-emerald-300",
  PROF: "text-amber-300",
  GEAR: "text-fuchsia-300",
  TRAIT: "text-orange-300",
  BUFF: "text-sky-300",
};
const isGearSource = (s) => s && s !== "NATURAL";

function srcTag(b) {
  if (b.prof) return "PROF";
  if (b.gear) return "GEAR";
  if (typeof b.name === "string" && b.name.startsWith("Lv")) return "LVL";
  return "BUFF";
}

// Build the full mathematical ledger for one stat: base → every source → effective.
export function buildStatLedger(fighter, statKey) {
  const base = fighter?.base?.[statKey] ?? null;
  const effective = statKey === "hp" ? (fighter?.max_hp ?? fighter?.hp) : fighter?.[statKey];
  const rows = [];
  for (const b of fighter?.applied_buffs || []) {
    const ap = b.apply || {};
    if (statKey in ap && ap[statKey] !== 0) {
      rows.push({ tag: srcTag(b), name: b.name, pct: Math.round(ap[statKey] * 100) });
    }
  }
  const attrKeys = { atk: ["atk_pct", "all_pct"], def: ["def_pct", "all_pct"], hp: ["hp_pct", "all_pct"], spd: ["spd_pct", "all_pct"] }[statKey] || [];
  for (const a of fighter?.attributes || []) {
    const e = a.effects || {};
    let pct = 0;
    for (const k of attrKeys) pct += e[k] || 0;
    if (pct !== 0) rows.push({ tag: "TRAIT", name: a.name, pct: Math.round(pct * 100) });
  }
  return { base, effective, rows };
}

const STAT_DEF = [
  { statKey: "atk", label: "ATK", Icon: Swords, color: "text-orange-300" },
  { statKey: "def", label: "DEF", Icon: Shield, color: "text-cyan-300" },
  { statKey: "spd", label: "SPD", Icon: Zap, color: "text-amber-300" },
];

// A stat chip whose hover reveals the layered "data ledger" of every source.
export function StatLedger({ fighter, statKey, label, Icon, color }) {
  const { base, effective, rows } = buildStatLedger(fighter, statKey);
  const up = base != null && effective > base;
  const down = base != null && effective < base;
  const tone = up ? "text-emerald-300 border-emerald-400/40" : down ? "text-rose-300 border-rose-400/40" : "text-white/85 border-white/12";
  return (
    <HoverCard openDelay={60} closeDelay={40}>
      <HoverCardTrigger asChild>
        <button
          data-testid={`stat-ledger-${statKey}`}
          className={cn("group inline-flex items-center gap-1 rounded-sm border bg-black/50 px-1.5 py-1 font-mono text-[11px] tabular-nums cursor-help transition-colors", tone)}
        >
          <Icon className={cn("h-3 w-3", up ? "text-emerald-300" : down ? "text-rose-300" : color)} />
          <span className="text-[8px] uppercase tracking-widest text-white/40">{label}</span>
          <span className="font-semibold">{effective}</span>
          {up && <TrendingUp className="h-2.5 w-2.5 text-emerald-300" />}
          {down && <TrendingDown className="h-2.5 w-2.5 text-rose-300" />}
        </button>
      </HoverCardTrigger>
      <HoverCardContent side="top" className="w-60 border-white/12 bg-[#0b0b12]/95 backdrop-blur-xl p-3 shadow-[inset_0_0_18px_rgba(0,229,255,0.08)]">
        <div className="flex items-center gap-1.5 mb-2">
          <Icon className={cn("h-3.5 w-3.5", color)} />
          <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/80">{label} Sources</span>
        </div>
        <div className="font-mono text-[11px] space-y-1">
          <div className="flex justify-between text-white/55"><span className="text-[9px] uppercase tracking-wider">[BASE] Mint</span><span>{base ?? "—"}</span></div>
          {rows.map((r, i) => (
            <div key={`${r.tag}:${r.name}`} className={cn("flex justify-between", r.pct >= 0 ? SRC_TONE[r.tag] || "text-white/70" : "text-rose-300")}>
              <span className="truncate pr-2"><span className="text-[9px] uppercase tracking-wider opacity-70">[{r.tag}]</span> {r.name}</span>
              <span>{r.pct >= 0 ? "+" : ""}{r.pct}%</span>
            </div>
          ))}
          {rows.length === 0 && <div className="text-white/30 text-[10px]">No modifiers — raw mint value.</div>}
          <div className="mt-1 border-t border-white/15 pt-1 flex justify-between font-bold text-white">
            <span className="text-[9px] uppercase tracking-wider">Effective</span><span>{effective}</span>
          </div>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}

// Layered fighter vitals: Anti-heal (grievous) lightning row over a blue clear
// shield row over the main HP bar. All three rows share the same width; each
// tracks the fighter's max HP as its scale (except shield, which is scaled to
// max_shield so a small shield reads correctly).
export function HpShieldBar({ hp, maxHp, shield = 0, maxShield = 0, grievous = 0 }) {
  const pct = Math.max(0, Math.min(100, (hp / Math.max(1, maxHp)) * 100));
  // Shield always shows against its OWN max, and always depletes from the RIGHT
  // (right side is where the shield "fills in" — as it drops, the remaining
  // blue clear bar shrinks from the left).
  const sMax = Math.max(1, maxShield || shield || 1);
  const sPct = Math.max(0, Math.min(100, (shield / sMax) * 100));
  // Grievous is scaled to max_hp — it caps at 100% (a 1× wound stack visually
  // covers the whole bar). Reads as "how much of the next heal will vanish".
  const gPct = Math.max(0, Math.min(100, (grievous / Math.max(1, maxHp)) * 100));
  const color = pct > 50 ? "bg-emerald-400" : pct > 25 ? "bg-amber-400" : "bg-rose-500";
  return (
    <div className="w-full">
      {/* Anti-heal (grievous wound) lightning row — top, shows only when > 0 */}
      {grievous > 0 && <LightningBar pct={gPct} amount={grievous} />}
      {/* Shield — blue clear bar, depletes from the right */}
      {(maxShield > 0 || shield > 0) && (
        <div
          className="relative h-1.5 rounded-sm bg-sky-500/[0.08] border border-sky-300/25 overflow-hidden mt-0.5"
          data-testid="shield-bar"
        >
          <motion.div
            className="absolute inset-y-0 left-0"
            style={{
              background: "linear-gradient(90deg, rgba(125,211,252,0.85) 0%, rgba(56,189,248,0.55) 100%)",
              boxShadow: "inset 0 0 6px rgba(125,211,252,0.55)",
            }}
            animate={{ width: `${sPct}%` }}
            transition={{ duration: 0.45 }}
          />
        </div>
      )}
      {/* Main HP row */}
      <div className="relative h-2.5 rounded-sm bg-black/70 border border-white/10 overflow-hidden mt-0.5">
        <motion.div className={cn("absolute inset-y-0 left-0", color)} animate={{ width: `${pct}%` }} transition={{ duration: 0.45 }} />
      </div>
      <div className="mt-0.5 flex items-center justify-between font-mono text-[9px]">
        <span className="flex items-center gap-2">
          {shield > 0 && <span className="text-sky-300" data-testid="shield-readout">⛨ {shield}{maxShield && maxShield !== shield ? `/${maxShield}` : ""}</span>}
          {grievous > 0 && <span className="text-fuchsia-300" data-testid="grievous-readout">☇ {grievous}</span>}
        </span>
        <span className="text-white/60 tabular-nums">{hp} / {maxHp}</span>
      </div>
    </div>
  );
}

// Jagged "moving lightning" stripe rendered as a repeating polyline. Width
// tracks the grievous-vs-maxHp ratio so it never overruns the HP bar. The
// dash-array drifts sideways to give it that live-wire feel.
function LightningBar({ pct, amount }) {
  return (
    <div className="relative h-1.5 rounded-sm bg-fuchsia-900/25 border border-fuchsia-400/25 overflow-hidden" data-testid="antiheal-bar" title={`Grievous wound · ${amount} HP of the next heal will be negated`}>
      <motion.div
        className="absolute inset-y-0 left-0"
        animate={{ width: `${pct}%` }}
        transition={{ duration: 0.45 }}
        style={{ overflow: "hidden" }}
      >
        <svg
          width="100%"
          height="100%"
          viewBox="0 0 200 8"
          preserveAspectRatio="none"
          className="absolute inset-0"
        >
          <defs>
            <linearGradient id="ah-glow" x1="0" x2="1" y1="0" y2="0">
              <stop offset="0%" stopColor="#f0abfc" />
              <stop offset="50%" stopColor="#e879f9" />
              <stop offset="100%" stopColor="#a855f7" />
            </linearGradient>
          </defs>
          <rect x="0" y="0" width="200" height="8" fill="url(#ah-glow)" opacity="0.35" />
          <motion.polyline
            fill="none"
            stroke="#f0abfc"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="miter"
            points="0,4 8,1 16,7 24,2 32,6 40,1 48,7 56,3 64,5 72,1 80,7 88,2 96,6 104,1 112,7 120,3 128,5 136,1 144,7 152,2 160,6 168,1 176,7 184,3 192,5 200,1"
            style={{ filter: "drop-shadow(0 0 3px #f472b6)" }}
            initial={{ strokeDashoffset: 0 }}
            animate={{ strokeDashoffset: [-40, 0] }}
            transition={{ duration: 0.8, repeat: Infinity, ease: "linear" }}
            strokeDasharray="6 4"
          />
        </svg>
      </motion.div>
    </div>
  );
}

// Compact stat-source HUD for the controlled fighter.
export function FighterLedger({ fighter, className = "" }) {
  if (!fighter) return null;
  const abilities = fighter.abilities_detail || [];
  return (
    <div className={cn("flex flex-wrap items-center gap-1.5", className)} data-testid="fighter-ledger">
      <StatLedger fighter={fighter} statKey="hp" label="HP" Icon={Star} color="text-emerald-300" />
      {STAT_DEF.map((s) => <StatLedger key={s.statKey} fighter={fighter} {...s} />)}
      {abilities.length > 0 && (
        <span className="inline-flex items-center gap-1 rounded-sm border border-fuchsia-400/30 bg-fuchsia-400/10 px-1.5 py-1 font-mono text-[10px] text-fuchsia-200">
          <Sparkles className="h-3 w-3" /> {abilities.length} special{abilities.length > 1 ? "s" : ""}
        </span>
      )}
    </div>
  );
}

const baseBtn = "relative h-auto py-2 px-2.5 flex flex-col items-start gap-0.5 text-left rounded-sm border bg-white/[0.03] transition-colors disabled:opacity-45 disabled:grayscale";

function CdOverlay({ cd }) {
  if (!cd || cd <= 0) return null;
  return (
    <div className="absolute inset-0 grid place-items-center rounded-sm bg-black/65 z-10">
      <span className="font-mono text-lg font-bold text-white/80">{cd}</span>
    </div>
  );
}

function Tag({ children, tone }) {
  return <span className={cn("absolute top-1 right-1 rounded-[2px] border px-1 font-mono text-[7px] uppercase tracking-wider leading-tight", tone)}>{children}</span>;
}

// The unified, "show everything" action grid used by Arena + Cavern.
// moves: [{id,name,desc,element,mult,kind,heal_pct,shield_pct,cd_add,current_cd,source}]
// abilities: [{id,name,desc,source,current_cd}]
// interrupt: {current_cd}|null
// allies: [{key,label,hp,maxHp,alive}]  — heal moves let you pick a target ally.
// onAttack(moveId, allyKey?) — allyKey passed only for heal moves.
const SUPPORT_META = {
  heal: { Icon: HeartPulse, tone: "hover:border-emerald-400/60 hover:bg-emerald-400/10", color: "#34d399", tag: "Heal" },
  shield: { Icon: ShieldPlus, tone: "hover:border-sky-400/60 hover:bg-sky-400/10", color: "#38bdf8", tag: "Defend" },
  disrupt: { Icon: Hourglass, tone: "hover:border-violet-400/60 hover:bg-violet-400/10", color: "#a78bfa", tag: "Disrupt" },
};
const supportSub = (m) => {
  if (m.kind === "heal") return `Heal ${Math.round((m.heal_pct || 0) * 100)}% HP`;
  if (m.kind === "shield") return `Shield ${Math.round((m.shield_pct || 0) * 100)}% HP`;
  if (m.kind === "disrupt") return `+${m.cd_add || 1}T enemy timers${m.slow ? " · slow" : ""}`;
  return "";
};

export function ActionGrid({
  moves = [], abilities = [], interrupt = null, allies = [],
  onAttack, onAbility, onDefend, onInterrupt,
  disabled = false, pending = false, stunned = false, testidPrefix = "act",
}) {
  const [healPick, setHealPick] = useState(null); // move id awaiting an ally target
  if (stunned) {
    return (
      <Button data-testid={`${testidPrefix}-stunned`} disabled={disabled || pending} onClick={onDefend}
        className="w-full bg-violet-500/30 text-violet-100 border border-violet-400/50 hover:bg-violet-500/40">
        <Zap className="h-4 w-4 mr-1.5" /> Stunned — pass turn
      </Button>
    );
  }
  const dis = disabled || pending;
  const livingAllies = allies.filter((a) => a.alive);
  const clickMove = (m) => {
    if (m.kind === "heal") {
      // If allies can be targeted, open the ally picker; else heal default (self/lowest).
      if (livingAllies.length > 0) setHealPick(m.id);
      else onAttack(m.id);
    } else {
      onAttack(m.id);
    }
  };
  return (
    <ScrollArea className="max-h-[44vh] pr-1">
      <motion.div
        className="grid grid-cols-2 gap-1.5"
        initial="hidden" animate="show"
        variants={{ show: { transition: { staggerChildren: 0.025 } } }}
      >
        {moves.map((m) => {
          const isSupport = m.kind && m.kind !== "damage";
          const sm = isSupport ? SUPPORT_META[m.kind] : null;
          const Icon = isSupport ? sm.Icon : (MOVE_ICONS[m.id] || ELEMENT_META[m.element]?.Icon || Swords);
          const el = ELEMENT_META[m.element];
          const cd = m.current_cd || 0;
          return (
            <motion.div key={m.id} variants={{ hidden: { opacity: 0, y: 6 }, show: { opacity: 1, y: 0 } }}>
              <button data-testid={`${testidPrefix}-move-${m.id}`} disabled={dis || cd > 0} onClick={() => clickMove(m)} title={m.desc}
                className={cn(baseBtn, "w-full", isSupport ? sm.tone : "hover:border-amber-400/50 hover:bg-amber-400/10",
                  isSupport ? "" : (m.id === "strike" ? "border-cyan-400/40" : "border-white/12"))}
                style={isSupport ? { borderColor: `${sm.color}50` } : undefined}>
                <CdOverlay cd={cd} />
                {isSupport
                  ? <Tag tone="text-white/70 border-white/20">{sm.tag}</Tag>
                  : (isGearSource(m.source) && <Tag tone={SRC_TONE.GEAR}>Gear</Tag>)}
                <span className="flex items-center gap-1.5 text-white text-[13px] font-medium leading-tight pr-10 truncate">
                  <Icon className="h-3.5 w-3.5 shrink-0" style={isSupport ? { color: sm.color } : (el ? { color: el.color } : undefined)} /> {m.name}
                </span>
                <span className="font-mono text-[9px] text-white/45 truncate w-full">
                  {isSupport ? supportSub(m) : `${el ? el.name : "Kinetic"} · ×${m.mult}${m.source ? ` · ${m.source}` : ""}`}
                </span>
              </button>
            </motion.div>
          );
        })}

        {abilities.map((ab) => {
          const meta = ABILITIES[ab.id] || { name: ab.name, Icon: Star, color: "text-fuchsia-300", desc: ab.desc };
          const Icon = meta.Icon;
          const cd = ab.current_cd || 0;
          const gear = isGearSource(ab.source);
          return (
            <motion.div key={`ab-${ab.id}-${ab.source}`} variants={{ hidden: { opacity: 0, y: 6 }, show: { opacity: 1, y: 0 } }}>
              <button data-testid={`${testidPrefix}-ability-${ab.id}`} disabled={dis || cd > 0} onClick={() => onAbility(ab.id)} title={meta.desc}
                className={cn(baseBtn, "w-full border-fuchsia-400/30 hover:border-fuchsia-400/60 hover:bg-fuchsia-400/10")}>
                <CdOverlay cd={cd} />
                <Tag tone={gear ? SRC_TONE.GEAR : SRC_TONE.NATURAL}>{gear ? "Gear" : "Innate"}</Tag>
                <span className={cn("flex items-center gap-1.5 text-[13px] font-medium leading-tight pr-10 truncate", meta.color)}>
                  <Icon className="h-3.5 w-3.5 shrink-0" /> {meta.name}
                </span>
                <span className="font-mono text-[9px] text-white/45 truncate w-full">
                  {gear ? ab.source : "Natural special"}
                </span>
              </button>
            </motion.div>
          );
        })}
      </motion.div>

      <div className="mt-1.5 grid grid-cols-2 gap-1.5">
        <Button data-testid={`${testidPrefix}-defend`} disabled={dis} onClick={onDefend} variant="outline"
          className="border-emerald-400/40 text-emerald-200 hover:bg-emerald-400/10 disabled:opacity-45">
          <Shield className="h-3.5 w-3.5 mr-1.5" /> Defend
        </Button>
        {interrupt && (
          <Button data-testid={`${testidPrefix}-interrupt`} disabled={dis || (interrupt.current_cd || 0) > 0} onClick={onInterrupt} variant="outline"
            className="border-violet-400/50 text-violet-200 hover:bg-violet-400/10 disabled:opacity-45">
            <Zap className="h-3.5 w-3.5 mr-1.5" /> Interrupt{(interrupt.current_cd || 0) > 0 ? ` (${interrupt.current_cd})` : ""}
          </Button>
        )}
      </div>

      {/* Heal target picker — hover/select an ally to receive the heal */}
      {healPick && (
        <motion.div
          data-testid={`${testidPrefix}-heal-targets`}
          initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
          className="mt-2 rounded-lg border border-emerald-400/40 bg-emerald-950/40 p-2 shadow-[inset_0_0_18px_rgba(52,211,153,0.12)]">
          <div className="flex items-center justify-between mb-1.5">
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-emerald-300 flex items-center gap-1">
              <HeartPulse className="h-3 w-3" /> Heal which ally?
            </span>
            <button data-testid={`${testidPrefix}-heal-cancel`} onClick={() => setHealPick(null)}
              className="text-white/40 hover:text-white"><X className="h-3.5 w-3.5" /></button>
          </div>
          <div className="grid grid-cols-1 gap-1">
            {livingAllies.map((a) => {
              const pct = Math.max(0, Math.min(100, (a.hp / Math.max(1, a.maxHp)) * 100));
              return (
                <button key={a.key} data-testid={`${testidPrefix}-heal-target-${a.key}`}
                  onClick={() => { onAttack(healPick, a.key); setHealPick(null); }}
                  className="group flex items-center gap-2 rounded-md border border-white/10 bg-black/40 px-2 py-1.5 text-left hover:border-emerald-400/60 hover:bg-emerald-400/10 transition-colors">
                  <span className="flex-1 truncate text-[12px] text-white/85">{a.label}</span>
                  <span className="w-20 h-1.5 rounded-sm bg-black/70 overflow-hidden">
                    <span className="block h-full bg-emerald-400" style={{ width: `${pct}%` }} />
                  </span>
                  <span className="font-mono text-[9px] text-white/55 tabular-nums w-14 text-right">{a.hp}/{a.maxHp}</span>
                </button>
              );
            })}
          </div>
        </motion.div>
      )}
    </ScrollArea>
  );
}
